import React, { useMemo, useState } from 'react';
import { useInquiryModalContext } from '../../Web/Inquiries/utils/InquiryModalContext';
import { CreateMessageModal } from '../ContentModals/CreateMessageModal';
import { useDispatch } from 'react-redux';
import {
  CREATE_INQUIRY_MESSAGE_ERROR,
  createInquiryMessage,
  UPLOAD_INQUIRY_ATTACHMENT,
  uploadInquiryAttachment,
} from '../../../redux/actions/inquiryActions';
import { useQueryClient } from 'react-query';
import { cacheKeys } from '../../../hooks/inquiries/cacheKeys';
import ResultModal from '../../../shared/components/Modals/MessageModal';

export function InquiryMessageModal() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {
    isMessageModalOpen,
    setIsMessageModalOpen,
    inquiryData,
    handleRefetchInquiries,
  } = useInquiryModalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [responseState, setResponseState] = useState({
    show: false,
    type: '',
    message: '',
  });

  const contextSubheading = useMemo(() => {
    const topicTitle = inquiryData?.topicTitle;
    const petName = inquiryData?.petName;

    if (!topicTitle || !petName) {
      return '';
    }

    return `Re: ${topicTitle} for ${petName}`;
  }, [inquiryData]);

  const handleSendMessage = async ({
    message,
    requiresResponse,
    attachments,
    onSuccess,
  }) => {
    if (
      !message &&
      attachments.images.length === 0 &&
      attachments.pdfs.length === 0
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const uploadedAttachments = { images: [], pdfs: [] };

      for (const image of attachments.images) {
        const result = await dispatch(uploadInquiryAttachment(image, 'images'));
        if (result.type === UPLOAD_INQUIRY_ATTACHMENT) {
          uploadedAttachments.images.push(result.payload.secure_url);
        }
      }

      for (const pdf of attachments.pdfs) {
        const result = await dispatch(uploadInquiryAttachment(pdf, 'pdfs'));
        if (result.type === UPLOAD_INQUIRY_ATTACHMENT) {
          uploadedAttachments.pdfs.push(result.payload.secure_url);
        }
      }

      const messageResult = await dispatch(
        createInquiryMessage({
          contactRequestId: inquiryData.id,
          message,
          requiresResponse,
          attachments: uploadedAttachments,
        }),
      );

      if (messageResult.type === CREATE_INQUIRY_MESSAGE_ERROR) {
        throw new Error('Failed to send message');
      }

      setResponseState({
        show: true,
        type: 'success',
        message: 'Message sent successfully!',
      });

      setIsMessageModalOpen(false);
      onSuccess?.();
      handleRefetchInquiries();

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });

        queryClient.invalidateQueries(
          cacheKeys.getInquiryMessages({
            inquiryId: inquiryData.id,
          }),
        );
      }, 2000);
    } catch (error) {
      console.error(error);

      setResponseState({
        show: true,
        type: 'danger',
        message: 'Failed to upload attachments or send message.',
      });

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CreateMessageModal
        isOpen={isMessageModalOpen}
        setIsOpen={setIsMessageModalOpen}
        contextSubheading={contextSubheading}
        onSave={handleSendMessage}
        isLoading={isLoading}
      />

      <ResultModal
        show={responseState.show}
        type="alert"
        alertType={responseState.type}
        message={responseState.message}
        onClose={() => setResponseState({ show: false, type: '', message: '' })}
      />
    </>
  );
}
